import React, { useState } from 'react';
import { LogOut, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/AuthContext';
import { endShift } from '@/lib/endShift';
import { toast } from '@/components/ui/use-toast';
import { useFeaturePermissions } from '@/lib/FeaturePermissionContext';

export default function EndShiftButton() {
  const { user, effectiveRole } = useAuth();
  const [busy, setBusy] = useState(false);
  const { canUseFeature } = useFeaturePermissions();
  if (effectiveRole !== 'rig' || !canUseFeature('end_shift')) return null;

  const handleEndShift = async () => {
    if (busy || !user?.id) return;
    const confirmed = window.confirm('End this shift and publish the current rig data to the supervisor?');
    if (!confirmed) return;
    setBusy(true);
    try {
      const result = await endShift(user.id);
      if (result.failed > 0 || result.pending > 0) {
        toast({ title: 'Shift ended with pending sync', description: `${result.pushed || 0} changes published. ${result.pending || 0} remain queued.`, variant: 'destructive' });
      } else {
        toast({ title: 'Shift ended', description: 'Current rig data has been published for supervisor access.' });
      }
    } catch (error) {
      toast({ title: 'End Shift failed', description: error?.message || 'The local data was kept and can be sent with Force Sync.', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      type="button"
      onClick={handleEndShift}
      disabled={busy}
      variant="outline"
      className="rounded-xl h-11 gap-2 bg-white dark:bg-slate-900 dark:border-slate-800"
      title="Publish this shift's data to supervisors"
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />}
      <span className="hidden sm:inline">End Shift</span>
    </Button>
  );
}
